import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import {
  BookingCalculationService,
  IBookingCalculationData,
  ICalculationOptions
} from '@roller/booking-calculation-service';
import { EProductTypes, IProduct } from '@core/product/models/product.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IProductSession } from '@core/product/models/product-session.model';
import { IDiscounts } from '@core/discounts/models/discounts.model';
import { IVenueCostSetting } from '@core/venue-config/models/venue-cost-setting.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { IBookingCostValidationResult } from '@core/booking/models/booking-cost-validation-result.model';
import { IProductPackageItem } from '@core/product/models/product-package-item.model';
import { OrderPartyPackageService } from '@core/order/order-party-package.service';
import { OrderCompulsoryTicketService } from '@core/order/order-compulsory-ticket.service';
import { OrderItemService } from '@core/order/order-item.service';
import { OrderPricingRuleService } from '@core/order/order-pricing-rule.service';
import { IOrder, IOrderAmounts, IOrderUserData } from '@core/order/models/order.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { OrderItemGroupService } from '@core/order/order-item-group.service';
import { OrderExistingBookingService } from '@core/order/order-existing-booking.service';
import {
  CMembership,
  IMembershipDetails,
  IMembershipUpgradesInfo,
  IUpcomingBookingDetails,
  TManageOrderType
} from '@app/tickets/tickets.model';
import { IOrderItemGroup } from '@core/order/models/order-item-group.model';
import { IGiftcardCanDeduct } from '@core/gift-card/gift-card-can-deduct.model';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { IWhenAttending } from '@app/when-attending/when-attending.model';
import { ISessionDiscount } from '@core/product/models/product-session-discount.model';
import { UpgradeService } from '@core/upgrade/upgrade.service';
import { IUpgradeFromTo, IVenueUpgradeItem } from '@core/upgrade/upgrade-hard-coded.model';
import { StorageService } from '@roller/rdl/utilities/storage';
import { CStorageKeys } from '@core/storage/storage-keys.const';
import { AppConfigService } from '@core/app-config/app-config.service';
import { OrderDiscountService } from '@core/order/order-discount.service';
import { OrderBookingService } from '@core/order/order-booking.service';
import { IBookingAPI } from '@core/booking/models/booking-api.model';
import { DiscountService } from '@core/discounts/discount.service';
import { IProductGiftcard, IProductPostage } from '@core/product/models/product-giftcard.model';
import { IGiftCardInfoForm } from '@app/checkout/guest-details/gift-card-form/gift-card-info-form.model';
import { ProductService } from '@core/product/product.service';
import { IBookingCreated } from '@core/booking/models/booking-created.model';
import { AnalyticsService } from '@core/analytics/analytics.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { LoggerService } from '@core/logger/logger.service';
import { IDialogFoodBeverageForm } from '@app/products/product/product-food-beverage/dialog-food-beverage-form.model';
import { OrderFoodBeverageService } from '@core/order/order-food-beverage.service';
import { OrderMembershipService } from '@core/order/order-membership.service';
import { IDraftBookingCreated } from '@core/booking/models/draft-booking-created.model';
import { CRouteList } from '@app/route-list.const';
import { IOrderItemChangedOptions } from '@core/order/models/order.service.model';
import { WhenAttendingSessionService } from '@app/when-attending/when-attending-session/when-attending-session.service';
import { getBlankOrder } from '@core/order/order.helpers';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { GuestCountService } from '../guest-count/guest-count.service';
import { DateFormatService } from '@app/core/date/date-format.service';
import { IProductPackageOptionSet } from '../product/models/product-option-set.model';
import { OrderProductOptionSetService } from './order-product-option-set.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  public order = this.emptyOrder();
  public order$ = new BehaviorSubject<IOrder>(this.order);

  constructor(
    private appConfigService: AppConfigService,
    private loggerService: LoggerService,
    private calculationService: BookingCalculationService,
    private venueConfigService: VenueConfigService,
    private upgradeService: UpgradeService,
    private storageService: StorageService,
    private productService: ProductService,
    private analyticsService: AnalyticsService,
    private navigationService: NavigationService,
    private discountService: DiscountService,
    private guestCountService: GuestCountService,
    private orderItemService: OrderItemService,
    private orderPartyPackageService: OrderPartyPackageService,
    private orderCompulsoryTicketService: OrderCompulsoryTicketService,
    private orderPricingRuleService: OrderPricingRuleService,
    private orderItemGroupService: OrderItemGroupService,
    private orderExistingBookingService: OrderExistingBookingService,
    private orderDiscountService: OrderDiscountService,
    private orderBookingService: OrderBookingService,
    private orderFoodBevService: OrderFoodBeverageService,
    private orderMembershipService: OrderMembershipService,
    private whenAttendingSessionService: WhenAttendingSessionService,
    private dateFormatService: DateFormatService,
    private orderProductOptionSetService: OrderProductOptionSetService
  ) {}

  getOrder$(): Observable<IOrder> {
    return this.order$.asObservable();
  }

  getOrder(): IOrder {
    return this.order;
  }

  getItems(): IOrderItem[] {
    return this.order.items;
  }

  getRelatedOrderItemsByKey(key: string): IOrderItem[] {
    return this.order.items.filter((i) => i.relatedOrderItemKey === key);
  }

  hasInvalidMinPurchaseItems(): boolean {
    return !!this.order.items.filter((item) => !!item.invalidItemMessage).length;
  }

  isItemRemovable(basketItem: IOrderItem | undefined): boolean {
    const removableItems = this.order.items.filter((item: IOrderItem) => !item.isForcedPurchase && item.quantity > 0);
    if (removableItems.length === 1) {
      return false;
    }

    if (!basketItem) {
      return true;
    }

    return !removableItems
      .filter((item) => item.uniqueId !== basketItem.uniqueId)
      .every((item) => item.addonToProductId === basketItem.parentProductId);
  }

  /** Converts an existing booking to order item groups, used to display as a booking summary */
  getOrderItemGroupsFromExistingBooking(booking: IUpcomingBookingDetails, products: IProduct[]): IOrderItemGroup[] {
    const order = this.emptyOrder();
    order.items = this.orderExistingBookingService.getOrderSummaryItemsFromBooking(booking, products);
    return this.orderItemGroupService.buildOrderItemGroups(order);
  }

  async getOrderFromExistingBooking(booking: IUpcomingBookingDetails, isComplete: boolean): Promise<IOrder> {
    const order = this.emptyOrder();
    order.items = await this.orderExistingBookingService.getOrderItemsFromExistingBooking(booking);
    order.discounts = booking.discounts;
    order.existingBooking = booking;
    const hasPartyPackage = booking.bookingItems.some((item) => item.productTypeId === EProductTypes.PartyPackage);
    if (hasPartyPackage) {
      order.partyName = booking.bookingName;
    }

    order.isPayFullPriceSelected = booking.amountOwing === 0;

    if (isComplete) {
      order.giftCards = this.orderExistingBookingService.getGiftCardsFromBooking(booking);
      order.amounts = this.orderExistingBookingService.getAmountsFromExistingBooking(booking);
      order.draftBooking = this.orderExistingBookingService.getDraftFromBooking(booking);
      order.isComplete = true;
    }

    return order;
  }

  getUniqueManageMembershipIds(items: IOrderItem[]): number[] {
    return [
      ...new Set(
        items
          .map((item: IOrderItem) => item.manageMembership?.membershipId)
          .filter((id) => id !== undefined) as number[]
      )
    ];
  }

  getMembershipNumbers(): string[] {
    return this.order.discounts
      .filter((discount) => discount.discountType === 'membership')
      .map((discount) => discount.code);
  }

  getBookingDTOFromOrder(order?: IOrder): Observable<IBookingAPI> {
    return this.orderBookingService.getBookingDTOFromOrder(order || this.order);
  }

  async getPriceCheck(): Promise<IOrder> {
    const bookingApi = await firstValueFrom(this.getBookingDTOFromOrder());
    const result = await firstValueFrom(this.discountService.checkBookingCost(bookingApi));
    return this.setOrderFromCosts(result);
  }

  getRemainderTotal() {
    if (!this.order.draftBooking) {
      return 0;
    }

    return (
      this.order.draftBooking.bookingCosts.total -
      this.order.draftBooking.amountOwing -
      this.order.amounts.giftCard -
      this.order.amounts.paid
    );
  }

  getDraftBooking(): IDraftBookingCreated | undefined {
    return this.order.draftBooking;
  }

  isEmpty(): boolean {
    return !this.order.items.length;
  }

  isEditBooking(): boolean {
    return !!this.order.existingBooking && !this.order.isComplete;
  }

  isDiscountPresent(): boolean {
    return this.order.discounts.length > 0;
  }

  isGiftCardPresent(): boolean {
    return this.order.giftCards.length > 0;
  }

  isInvalidPlacedOrder(order: IOrder, url?: string): boolean {
    const freshOrderOnlyRoutes = [CRouteList.products, CRouteList.home];
    const isFreshOrderRequired = !!url && freshOrderOnlyRoutes.some((route) => url.includes(route));
    return !!order.isComplete && !!order.draftBooking && isFreshOrderRequired;
  }

  isFullyDiscountedOrGiftCard(): boolean {
    return this.isNothingToPay() && (this.isDiscountPresent() || this.isGiftCardPresent());
  }

  isFullyDiscounted(): boolean {
    return this.isNothingToPay() && this.isDiscountPresent();
  }

  private isNothingToPay(): boolean {
    return this.order.amounts.total <= 0;
  }

  isAddItemAllowed(): boolean {
    if (!this.isEditBooking()) {
      return true;
    }

    return this.order.items.some((item) => item.existingBookingItem?.canAddItems);
  }

  isDiscountsVisible(currentRoute: string): boolean {
    const routes = this.orderDiscountService.getDiscountVisibleRoutes();
    return routes.some((r) => currentRoute.includes(r)) || !!this.order.isComplete;
  }

  isMembershipUpgradeFlow(): boolean {
    return this.getOrder().items[0]?.manageBookingType === CMembership;
  }

  /** Determine if we should display the order as if only a deposit was to be paid */
  isPayingByDeposit(): boolean {
    const canPayDeposit = this.orderItemService.orderItemsAllowDeposit(this.order.items);
    return canPayDeposit && !this.order.isPayFullPriceSelected;
  }

  /** We only need to hit the /costs endpoint if there are discounts present, and we are later in the checkout workflow */
  isCostCheckRequired(): boolean {
    const apiCostRoutes = this.orderDiscountService.getDiscountVisibleRoutes();
    const currentRoute = this.navigationService.getCurrentURL();
    const isValidRoute = apiCostRoutes.some((r) => currentRoute?.includes(r));

    return !this.isEmpty() && (this.isDiscountPresent() || this.isGiftCardPresent()) && isValidRoute;
  }

  isDraftBookingVisibleRoute(): boolean {
    return !!this.navigationService.getCurrentURL()?.includes(CRouteList.payment);
  }

  setDraftBooking(draftBooking: IDraftBookingCreated | undefined): IOrder {
    this.order.draftBooking = draftBooking;
    if (draftBooking) {
      this.order.amounts = this.orderBookingService.getAmountsFromBookingCosts(this.order, draftBooking.bookingCosts);
    }

    return this.publishOrder();
  }

  setEditMembershipItem(membership: IOrderItem | undefined) {
    this.order.editMembershipItem = membership;
    this.publishOrder();
  }

  setUpdateOrderTime(date: Date) {
    this.order.lastUpdatedDate = date;
    this.publishOrder();
  }

  async setTip(tip: number) {
    this.order.tip = tip;
    await this.updateOrder();
  }

  setOrderComplete(complete: boolean) {
    this.order.isComplete = complete;
    this.publishOrder();
  }

  async setDiscounts(discounts: IDiscounts[]) {
    this.order.discounts = discounts;
    await this.updateOrder();
  }

  async setGiftCards(giftCards: IGiftcardCanDeduct[]) {
    this.order.giftCards = giftCards;
    await this.updateOrder();
  }

  setUserData(userData: IOrderUserData) {
    this.order.userData = userData;
    this.publishOrder();
  }

  async setIsPayFullPriceSelected(value: boolean) {
    this.order.isPayFullPriceSelected = value;
    await this.updateOrder();
  }

  async setGiftCardPostage(
    postage: IProductPostage | undefined,
    uniqueId: string,
    settings: IProductGiftcard | undefined,
    giftCardUserInfo: IGiftCardInfoForm[]
  ) {
    const giftCardItem: IOrderItem | undefined = this.order.items.find(
      (item: IOrderItem) => item.productType === 'giftcard' && item.productId === settings?.productId
    );
    if (!giftCardItem?.uniqueGiftCards || !settings) {
      return;
    }

    const uniquePostage = giftCardItem.uniqueGiftCards.find((uni) => uni.formId === uniqueId);
    if (!uniquePostage) {
      this.loggerService.warn(`Error unique form card not found: ${uniqueId}, postageId: ${postage?.productPostageId}`);
      return;
    }

    this.setUserData({ ...this.order.userData, giftCardUserInfo });
    uniquePostage.postage = postage;
    this.updateOrderItem(giftCardItem);
    await this.updateOrder();
  }

  setOrderFromCosts(
    dto: IBookingCostValidationResult,
    discounts?: IDiscounts[],
    giftCards?: IGiftcardCanDeduct[]
  ): IOrder {
    this.order.discounts = this.orderDiscountService.getCostValidDiscounts(dto, discounts || this.order.discounts);
    this.order.giftCards = this.orderDiscountService.getCostValidGiftCards(dto, giftCards || this.order.giftCards);
    this.order.amounts = this.orderBookingService.getAmountsFromBookingCosts(this.order, dto.bookingCosts);

    return this.publishOrder();
  }

  /** When a booking is fully discounted/gift carded then merge the results to ensure no fees, and other line items show.  */
  async setOrderFromFullyDiscounted(booking: IBookingCreated) {
    this.setOrderComplete(true);
    this.order.amounts = {
      ...this.order.amounts,
      tax: booking.totalTaxAmount,
      taxExclusive: booking.totalTaxExclusiveOnly,
      total: booking.totalBookingAmount,
      creditCardFees: 0,
      fees: 0
    };

    await this.updateOrder();
  }

  async refreshOrder() {
    await this.updateOrder();
  }

  loadStorageOrder(): IOrder | undefined {
    const storedOrder = this.storageService.getItem<IOrder>(CStorageKeys.order, false);
    if (!this.isStoredOrderValid(storedOrder)) {
      this.clearStorageOrder();
      return;
    }

    this.order = storedOrder;
    this.order.createdDate = new Date(storedOrder.createdDate);
    this.order.items = this.order.items.map((item) => ({
      ...item,
      bookingDate: new Date(item.bookingDate),
      packageOrderItems: item.packageOrderItems?.map((packageItem) => ({
        ...packageItem,
        bookingDate: new Date(packageItem.bookingDate)
      }))
    }));
    this.publishOrder();

    return this.order;
  }

  /**
   * Adds, removes, or updates an item in the cart, with analytics
   * Auto add compulsory tickets / add force addons, auto-remove dependant items when an item is removed
   * Sends one analytic event based on the items removed/added
   */
  async addRemoveUpdateItem(options: IOrderItemChangedOptions): Promise<IOrderItem> {
    const relatedOrderItems = this.getRelatedOrderItemsByKey(options.relatedOrderItemKey);
    const parentUniqueId = options.isAddon
      ? relatedOrderItems.find((relatedItem: IOrderItem) => !relatedItem.addonToProductId)?.uniqueId
      : undefined;
    const quantity = options.change.ev.quantity;
    const item = this.orderItemService.createOrderItem({ ...options, relatedOrderItems, parentUniqueId });

    const existingItem = this.orderItemService.getMatchingOrderItemByItem(this.order, item);
    const isAddition = !existingItem || quantity > existingItem.quantity;
    /** The removal flow will handle all related items and analytics, so we only need to handle the item being removed here */
    if (existingItem && quantity === 0) {
      return this.removeItemByItem(existingItem, false);
    }

    const totalBeforeUpdate = this.order.amounts.total;
    if (existingItem) {
      this.updateItemQuantity(existingItem, quantity);
    } else {
      this.addItem(item);
    }

    const createdOrUpdatedItem: IOrderItem = existingItem || item;

    // calculate compulsory tickets before addon requirements as this can affect required addons
    const compulsory: IOrderItem[] = this.handleCompulsoryTickets(options, createdOrUpdatedItem);
    await this.autoAddOrUpdateItems(compulsory);
    const forcedAddons: IOrderItem[] = this.handleForcedAddons(options, createdOrUpdatedItem);
    await this.autoAddOrUpdateItems(forcedAddons);

    // run analytics after all changes, so we only get one event for the entire list of items added/updated
    await this.onOrderItemChanged(createdOrUpdatedItem, isAddition, false);
    this.onItemChangeAnalytics(isAddition, [...compulsory, ...forcedAddons, createdOrUpdatedItem], totalBeforeUpdate);

    return item;
  }

  /** When an F&B item form is submitted, create the item and add, remove, or update (replace). */
  onFoodAndBeverageItemChanged(form: IDialogFoodBeverageForm, parentProduct: IProduct, relatedOrderItemKey?: string) {
    const item = this.orderFoodBevService.createOrderItem(form, parentProduct, relatedOrderItemKey);
    const existingItem = this.order.items.find((orderItem) => orderItem.uniqueId === item.uniqueId);
    const isAddition = !existingItem || item.quantity > existingItem.quantity;

    if (existingItem && item.quantity === 0) {
      return this.removeItemByItem(existingItem);
    } else if (existingItem) {
      this.updateOrderItem(item);
    } else {
      this.addItem(item);
    }

    return this.onOrderItemChanged(item, isAddition);
  }

  async setOrderFromMembership(
    memberships: IMembershipDetails[],
    products: IProduct[],
    upgrades: IMembershipUpgradesInfo[],
    type: TManageOrderType
  ): Promise<IOrder> {
    this.order = this.emptyOrder();
    this.order.items = this.orderMembershipService.getOrderItemsFromMemberships(memberships, products, upgrades, type);
    await this.updateOrder();
    return this.order;
  }

  setOrderFromMembershipRenewal(
    productIds: string[],
    parentProduct: IProduct,
    renewalBookingUniqueId: string | undefined
  ): Promise<IOrder> {
    this.order = this.emptyOrder();
    this.order.items = this.orderMembershipService.getOrderItemsForRenewal(
      productIds,
      parentProduct,
      renewalBookingUniqueId
    );

    return this.updateOrder();
  }

  /** Creates a new order item based on the one we are upgrading, and replaces it in the cart */
  onUpgradeEditMembership(product: IProductInfo, parentProduct: IProduct, upgrades: IMembershipUpgradesInfo[]) {
    if (!this.order.editMembershipItem) {
      return;
    }

    const upgradeItem = this.orderMembershipService.buildUpgradedOrderItem(
      product,
      parentProduct,
      upgrades,
      this.order.editMembershipItem
    );

    this.updateOrderItem(upgradeItem);
    this.order.editMembershipItem = undefined;
    return this.onOrderItemChanged(upgradeItem, true);
  }

  async onReschedule(bookingDate: Date, editItem?: IOrderItem, session?: IProductSession): Promise<IOrder> {
    const items = this.orderExistingBookingService.getRelatedRescheduleItems(this.getItems(), editItem);
    const rescheduled = this.orderItemService.buildRescheduledItems(items, bookingDate, session);

    await Promise.all(
      rescheduled.map(async (item) => {
        this.updateOrderItem(item);
        return await this.onOrderItemChanged(item, false, false);
      })
    );

    return this.publishOrder();
  }

  /** When in the product dialog, we don't want to automatically remove all min purchase items when one is removed - we only want this behaviour from the cart */
  async removeItemByItem(item: IOrderItem, includeMinPurchaseItems = true): Promise<IOrderItem> {
    const relatedOrderItems = this.getRelatedOrderItemsByKey(item.relatedOrderItemKey);
    this.removeItem(item);
    const removedItems = this.handleRemovedItem(item, relatedOrderItems, includeMinPurchaseItems);
    // Run analytics manually here so we only get one event for the entire list of items removed
    const totalBeforeUpdate = this.order.amounts.total;
    await this.onOrderItemChanged(item, false, false);
    this.onItemChangeAnalytics(false, removedItems, totalBeforeUpdate);
    return item;
  }

  handleCompulsoryTickets(options: IOrderItemChangedOptions, createdOrUpdatedItem: IOrderItem): IOrderItem[] {
    const tickets = options.isAddon ? options.change.parentProduct.products : options.associatedTickets;
    if (!tickets?.some((info) => info.forceMinPurchase)) {
      return [];
    }

    const relatedOrderItems: IOrderItem[] = this.getRelatedOrderItemsByKey(options.relatedOrderItemKey);
    // Get all compulsory ticket changes required, excluding any removals which is checked on order item removal
    return this.orderCompulsoryTicketService.getCompulsoryTicketChanges({
      ...options,
      order: this.order,
      relatedOrderItems,
      changedItem: createdOrUpdatedItem
    });
  }

  /** When an item is added/updated/or removed, this ensures forced addon requirements are met - including any compulsory tickets added */
  handleForcedAddons(options: IOrderItemChangedOptions, orderItem: IOrderItem): IOrderItem[] {
    if (!options.forcedPurchaseAddons?.length) {
      return [];
    }

    const relatedOrderItems = this.getRelatedOrderItemsByKey(options.relatedOrderItemKey);
    const minForceCount = this.orderCompulsoryTicketService.calculateMinForcedAddon([orderItem], relatedOrderItems);
    const selectedSession = this.whenAttendingSessionService.getSelectedSession(
      options.selectedSession,
      options.associatedTickets?.find((ticket: IProductInfo) => ticket.id === orderItem.productId)
    );

    // Get all forced addon changes required as order items, excluding any removals which is checked on order item removal
    return this.orderCompulsoryTicketService.getForcedPurchaseBasket(
      minForceCount,
      orderItem,
      relatedOrderItems,
      options.forcedPurchaseAddons,
      options.relatedOrderItemKey,
      options.bookingDate,
      selectedSession
    );
  }

  setPartyName(name: string) {
    this.order.partyName = name;
    this.publishOrder();
  }

  setTableNumber(tableNumber?: string) {
    this.order.tableNumber = tableNumber;
    this.publishOrder();
  }

  setExternalId(externalId?: string) {
    this.order.externalId = externalId;
    this.publishOrder();
  }

  /**
   * Converts an existing booking to an order and updates the central order on the service, letting subscribers know of the change
   * This can be used to edit an existing booking or set it as display-only by passing isComplete
   */
  async setOrderFromExistingBooking(booking: IUpcomingBookingDetails, isComplete: boolean): Promise<IOrder> {
    this.order = await this.getOrderFromExistingBooking(booking, isComplete);
    return await this.updateOrder();
  }

  setSessionDiscount(
    parentProduct: IProduct,
    selectedDate: IWhenAttending | undefined,
    selectedSession: IProductSession | undefined,
    product: IProductInfo,
    discount: ISessionDiscount
  ) {
    const existingItem = this.orderItemService.getMatchingOrderItem(
      this.order.items,
      parentProduct,
      selectedDate,
      selectedSession,
      product,
      product?.uniqueId
    );
    if (!existingItem) {
      return;
    }

    existingItem.sessionDiscount = discount;
    existingItem.product.appliedSessionDiscount = discount;
    this.updateOrderItem(existingItem);

    return this.onOrderItemChanged(existingItem, true);
  }

  setUserDefinedValueItem(
    parentProduct: IProduct,
    selectedDate: IWhenAttending | undefined,
    selectedSession: IProductSession | undefined,
    product: IProductInfo,
    userDefinedValue: number
  ): Promise<IOrderItem> | undefined {
    const existingItem = this.orderItemService.getMatchingOrderItem(
      this.order.items,
      parentProduct,
      selectedDate,
      selectedSession,
      product,
      product?.uniqueId,
      true
    );
    if (!existingItem) {
      return;
    }

    existingItem.cost = userDefinedValue;
    existingItem.product.cost = userDefinedValue;
    this.updateOrderItem(existingItem);

    return this.onOrderItemChanged(existingItem, true);
  }

  onPartyPackageVariationChanged(
    ev: INumberIncrementProductItem,
    packageItem: IProductPackageItem,
    session?: IProductSession
  ): Promise<IOrderItem> | undefined {
    const event = this.orderPartyPackageService.onOrderItemSelection(this.order, ev, packageItem, session);
    if (event?.item) {
      this.updateOrderItem(event.item);
      return this.onOrderItemChanged(event.item, event.isAddition);
    }

    return;
  }

  onProductOptionSetChanged(
    ev: INumberIncrementProductItem,
    productOptionSet: IProductPackageOptionSet,
    date: Date,
    session?: IProductSession
  ): Promise<IOrderItem> | undefined {
    const event = this.orderProductOptionSetService.onOrderItemSelection(
      this.order,
      ev,
      productOptionSet,
      date,
      session
    );
    if (event?.item) {
      this.updateOrderItem(event.item);
      return this.onOrderItemChanged(event.item, event.isAddition);
    }

    return;
  }

  autoUpdatePackageItemSelection(
    orderItem: IOrderItem,
    packageItems: IProductPackageItem[]
  ): Promise<IOrderItem> | undefined {
    const item = this.orderPartyPackageService.setRequiredSelection(packageItems, orderItem);
    this.updateOrderItem(item);
    return this.onOrderItemChanged(item, false, false);
  }

  autoUpdateOptionSetSelections(
    orderItem: IOrderItem,
    optionSetSelections: IProductPackageOptionSet[]
  ): Promise<IOrderItem> | undefined {
    const item = this.orderProductOptionSetService.setRequiredSelection(optionSetSelections, orderItem);
    this.updateOrderItem(item);
    return this.onOrderItemChanged(item, false, false);
  }

  async removeDiscount(discount: IDiscounts) {
    if (!this.order.discounts.length) {
      return;
    }

    this.order.discounts = this.order.discounts.filter((d) => d.code !== discount.code);
    await this.updateOrder();
  }

  async removeGiftCard(giftCard: IGiftcardCanDeduct) {
    if (!this.order.giftCards?.length) {
      return;
    }

    this.order.giftCards = this.order.giftCards.filter((g) => g.giftcardNumber !== giftCard.giftcardNumber);
    await this.updateOrder();
  }

  async clearDiscounts() {
    this.order.discounts = [];
    await this.updateOrder();
  }

  async clearGiftCards() {
    this.order.giftCards = [];
    await this.updateOrder();
  }

  /** Replaces clearCheckoutBasket */
  clearOrder() {
    if (this.isEditBooking()) {
      this.productService.clearProductAvailability();
    }

    this.clearStorageOrder();
    this.order = this.emptyOrder(this.getPreservedTableNumber());
    this.publishOrder();
  }

  /** When an upgrade offer is accepted by guest it removes the original item(s) and replaces them with the upgraded ticket items */
  onUpgradeOfferSelected(offer: IVenueUpgradeItem) {
    offer.ticketMatch.forEach((upgrade: IUpgradeFromTo) => {
      const item = this.upgradeService.getUpgradeOrderItem(this.order.items, upgrade);
      if (!item || !offer.toProduct || !upgrade.toTicket) {
        return;
      }

      const increment = this.upgradeService.getUpgradeIncrement(item, upgrade);
      const orderItem = this.orderItemService.buildOrderItem(
        increment,
        offer.toProduct,
        item.bookingDate,
        item.session,
        [],
        item.relatedOrderItemKey
      );

      this.removeItem(item);
      this.addItem(orderItem);
      this.onOrderItemChanged(item, false);
    });
  }

  getPerGuestItems() {
    return this.getItems().filter((item: IOrderItem) => {
      const hasPerGuestPackageItems =
        item.productType === 'package' && item.packageOrderItems?.some((packageItem) => packageItem.isPerGuestItem);

      // Items that have a quantity of 0 are being removed so we don't need to consider them
      return (item.isPerGuestItem || hasPerGuestPackageItems) && item.quantity > 0;
    });
  }

  removePerGuestItems() {
    this.getPerGuestItems().forEach((item) => this.removeItemByItem(item));
  }

  getConquerorItems() {
    return this.getItems().filter((item) => {
      const hasPackageItemsLinkedToConqueror = item.packageOrderItems?.some(
        (packageItem) => packageItem.isLinkedToConqueror
      );

      return (item.isLinkedToConqueror || hasPackageItemsLinkedToConqueror) && item.quantity > 0;
    });
  }

  removeConquerorItems() {
    this.getConquerorItems().forEach((item) => this.removeItemByItem(item));
  }

  hasInvalidAutomaticDiscount() {
    return this.order.discounts.some(
      (discount) =>
        discount.code === this.venueConfigService.getVenueConfig().automaticDiscount?.code && discount.validationMessage
    );
  }

  /** Common calculations and functions that should take place when an order item is changed */
  private async onOrderItemChanged(item: IOrderItem, isAddition: boolean, analytics = true): Promise<IOrderItem> {
    if (this.isEditBooking() || this.order.items.some((i) => i.isManageItemModified)) {
      this.order.isManagedOrderModified = true;
    }

    const totalBeforeUpdate = this.order.amounts.total;
    this.order.tip = 0;
    await this.updateOrder();

    if (analytics) {
      this.onItemChangeAnalytics(isAddition, [item], totalBeforeUpdate);
    }

    return item;
  }

  /** Recalculate common properties when an item or key data has changed */
  private async updateOrder(): Promise<IOrder> {
    this.order.uniqueId = hexGenerate(64);
    this.order.items = this.orderPricingRuleService.getItemsWithPricingRuleData(
      this.order.items,
      this.venueConfigService.getVenueConfig(),
      this.getExistingBookingCreatedDate()
    );
    this.order.items = this.orderItemService.getItemsWithInvalidMessage(this.order.items);
    this.order.itemCount = this.getOrderItemCount();
    this.order.amounts = await this.getAmounts();
    this.order.isContinueCheckoutDisabled = this.getIsContinueCheckoutDisabled(this.order.amounts);

    return this.publishOrder();
  }

  /** Notify subscribers of the order change */
  private publishOrder(): IOrder {
    this.order.lastUpdatedDate = new Date();
    this.publishOrderItemGroups();
    this.setStoredOrder(this.order);
    this.order$.next(this.order);
    return this.order;
  }

  private setStoredOrder(order: IOrder) {
    this.storageService.setItem(CStorageKeys.order, JSON.stringify(order), false);
  }

  private getIsContinueCheckoutDisabled(amounts: IOrderAmounts) {
    if (this.hasInvalidMinPurchaseItems()) {
      return true;
    }

    const maxRefund = this.venueConfigService.getVenueConfig().venueSettings.maxRefund;
    return this.isEditBooking() && amounts.total < 0 && (maxRefund <= 0 || -amounts.total > maxRefund);
  }

  private getOrderItemCount(): number {
    return this.order.items
      .map((item: IOrderItem) => item.quantity)
      .reduce((prev: number, next: number) => prev + next, 0);
  }

  private publishOrderItemGroups() {
    const groups: IOrderItemGroup[] = this.orderItemGroupService.buildOrderItemGroups(this.order);
    this.orderItemGroupService.setOrderItemGroups(groups);
  }

  /**
   * 1. The order is complete, either placed or created from a booking as read-only - don't change the amounts
   * 2. We have discounts/giftCards applied and later in the checkout workflow, hit the /costs endpoint
   * 3. We are on a draft booking page e.g. payments with a draft booking set - should always use the API provided costs
   * 4. We have no discounts/giftCards i.e.  in early stages of checkout - either way we don't include discounts/giftCards in the calculation
   */
  private async getAmounts(): Promise<IOrderAmounts> {
    if (this.order.isComplete) {
      return this.order.amounts;
    }

    if (this.isCostCheckRequired()) {
      return await this.getAmountsFromApi();
    }

    if (this.isDraftBookingVisibleRoute() && this.order.draftBooking) {
      return this.orderBookingService.getAmountsFromBookingCosts(this.order, this.order.draftBooking.bookingCosts);
    }

    return this.getAmountsFromBookingCalc();
  }

  /** In CX, we rely fully on the `/cost` endpoint to calculate discount & gift-card effect, so we don't include them here. This covers pre-cost endpoint workflows */
  private getAmountsFromBookingCalc() {
    const paidExcludingFees = this.order.existingBooking?.amountPaidExcludingCardFees || 0;
    const paidCreditFee = this.order.existingBooking?.creditCardFee || 0;
    const paidTransactionFee = this.order.existingBooking?.transactionFee || 0;
    const paid = paidExcludingFees + paidCreditFee + paidTransactionFee;

    const data: IBookingCalculationData = {
      items: this.orderItemService.getItemsForCalculations(this.order.items, this.isEditBooking()),
      costSettings: this.getCostSettings(),
      tip: this.order.tip,
      paid: paidExcludingFees
    };

    const considerDeposit = !this.order.isPayFullPriceSelected;
    const options: ICalculationOptions = {
      considerDeposit,
      guestPayFees: this.venueConfigService.getVenueConfig().paymentPage.guestPayFees
    };
    const depositAmounts = this.calculationService.getAmounts(data, { ...options, considerDeposit: true });
    let amounts: IOrderAmounts = {
      paid,
      paidExcludingFees,
      amountIfDepositOnly: depositAmounts.deposit,
      ...this.calculationService.getAmounts(data, options)
    };

    if (considerDeposit && this.orderItemService.orderItemsAllowDeposit(this.order.items) && amounts.total < 0) {
      if (amounts.totalIgnoringDeposit < 0) {
        // overpaid, ignore deposit
        amounts = { ...amounts, ...this.calculationService.getAmounts(data, { ...options, considerDeposit: false }) };
      } else {
        // we don't issue refunds if the deposit decreases
        amounts.total = 0;
      }
    }

    this.order.items = this.getCalculatedCostItems();

    return amounts;
  }

  /**
   * When we are in the details/discount portion of the checkout workflow, hit the /costs endpoint for calculations and booking cost data
   * Update active discounts with changes i.e. becomes invalid or amount changes
   * Update local amounts via calculation as sub-total is not included with the `/cost` endpoint so may not reflect correctly
   */
  private async getAmountsFromApi(): Promise<IOrderAmounts> {
    const bookingApi = await firstValueFrom(this.getBookingDTOFromOrder());
    const result = await firstValueFrom(this.discountService.checkBookingCost(bookingApi));
    this.order.discounts = this.orderDiscountService.getCostValidDiscounts(result, this.order.discounts);
    this.order.giftCards = this.orderDiscountService.getCostValidGiftCards(result, this.order.giftCards);
    this.order.amounts = this.getAmountsFromBookingCalc();

    return this.orderBookingService.getAmountsFromBookingCosts(this.order, result.bookingCosts);
  }

  private getCostSettings() {
    const costSettings: IVenueCostSetting = structuredClone(this.venueConfigService.getCostSettings());

    if (this.isEditBooking()) {
      costSettings.transactionFeePercent = 0;
      costSettings.feePerTransaction = 0;
    }

    return costSettings;
  }

  private getCalculatedCostItems(): IOrderItem[] {
    return this.order.items.map((item) => {
      return {
        ...item,
        totalCost: this.calculationService.getTicketCost(item) * item.quantity
      };
    });
  }

  private emptyOrder(tableNumber?: string): IOrder {
    return { ...getBlankOrder(), tableNumber };
  }

  private clearStorageOrder() {
    this.storageService.removeItem(CStorageKeys.order, false);
    this.guestCountService.clearGuestCount();
  }

  /** Update quantity and re-set gift card information for this product */
  private updateItemQuantity(item: IOrderItem, quantity: number) {
    item.oldQuantity = item.quantity;
    item.quantity = quantity;
    item.uniqueGiftCards = this.orderItemService.getUniqueGiftCards(item.quantity, item.productType);
    this.order.userData.giftCardUserInfo = this.getGiftCardUserInfoOnItemUpdated(item);
    this.updateOrderItem(item);
  }

  private updateOrderItem(item: IOrderItem) {
    const itemIndex = this.order.items.findIndex((i) => i.uniqueId === item.uniqueId);
    if (itemIndex === -1) {
      return;
    }

    this.order.items[itemIndex] = this.getItemEventChanges(item);
  }

  private addItem(item: IOrderItem) {
    this.order.items.push(this.getItemEventChanges(item));
  }

  /** Adjust the item with common workflow properties whenever its removed,added,updated */
  private getItemEventChanges(item: IOrderItem): IOrderItem {
    return {
      ...item,
      isManageItemModified: item.isManageItemModified ?? this.isEditBooking()
    };
  }

  private getGiftCardUserInfoOnItemUpdated(item: IOrderItem): IGiftCardInfoForm[] | undefined {
    if (!this.order.userData.giftCardUserInfo?.length) {
      return undefined;
    }

    return this.order.userData.giftCardUserInfo.filter((form) => item.productId !== form.productId);
  }

  private onItemChangeAnalytics(isAddToCart: boolean, orderItems: IOrderItem[], oldTotal: number) {
    const totalDifference = Math.floor((this.order.amounts.total - oldTotal) * 100) / 100;
    this.analyticsService.onCartEvents(
      isAddToCart ? 'add_to_cart' : 'remove_from_cart',
      orderItems,
      totalDifference,
      this.order.amounts.total,
      this.navigationService.getCurrentURL() || '',
      this.getItemChangeAnalyticLocation()
    );
  }

  private getItemChangeAnalyticLocation(): string {
    return this.navigationService.currentRouteContains([CRouteList.productPage])
      ? 'product_dialog'
      : 'checkout_summary';
  }

  private autoAddOrUpdateItems(items: IOrderItem[]): Promise<IOrderItem[]> {
    return Promise.all(
      items.map(async (item) => {
        const existingItem = this.orderItemService.getMatchingOrderItemByItem(this.order, item);
        if (!existingItem && item.quantity === 0) {
          return item;
        }

        existingItem ? this.updateItemQuantity(existingItem, item.quantity) : this.addItem(item);
        const isAddition = !existingItem || item.quantity > existingItem?.quantity;

        return this.onOrderItemChanged(existingItem || item, isAddition, false);
      })
    );
  }

  /**
   * We want to go through the update quantity flow first in-case the item remains in cart and is used for analytics
   * Don't fully remove 'existing booking edit items in the cart so we can inform the backend of its new quantity with the booking update.
   */
  private removeItem(item: IOrderItem) {
    this.updateItemQuantity(item, 0);
    if (this.isEditBooking() && item.existingBookingItem) {
      return;
    }

    this.order.items = this.order.items.filter((i) => i.uniqueId !== item.uniqueId);
  }

  /** Get the list of auto-removed items, including the one being removed */
  private handleRemovedItem(
    item: IOrderItem,
    relatedOrderItems: IOrderItem[],
    includeMinPurchaseItems: boolean
  ): IOrderItem[] {
    const items = this.orderItemService.getAffectedRemovableItems(item, relatedOrderItems, includeMinPurchaseItems);
    items.forEach((item) => (item.quantity > 0 ? this.updateOrderItem(item) : this.removeItem(item)));
    return items;
  }

  /** Retain the table number for orders that have just been placed so the guest doesn't need to re-enter it if they want to place another order */
  private getPreservedTableNumber(): string | undefined {
    if (this.isEditBooking() || this.isMembershipUpgradeFlow()) {
      return undefined;
    }

    return this.order.tableNumber;
  }

  private isStoredOrderValid(order: IOrder | string | null): order is IOrder {
    if (!order || typeof order === 'string') {
      return false;
    }

    if (!this.isOrderAgeValid(order)) {
      return false;
    }

    /** If an order is just placed, and we re-visit the start of the flow we shouldn't hydrate it - i.e. we re-scan a table number */
    if (this.isInvalidPlacedOrder(order, this.navigationService.getCurrentURL())) {
      return false;
    }

    /** We want to hydrate orders even with no items if it contains gift cards / table number that the guest has applied */
    if (order.discounts?.length || order.giftCards?.length || order.tableNumber) {
      return true;
    }

    return !!order.items.length;
  }

  private isOrderAgeValid(order: IOrder): boolean {
    const lastUpdate = new Date(order.lastUpdatedDate);
    const now = new Date();
    const timeDelta = now.getTime() - lastUpdate.getTime();
    return timeDelta < this.appConfigService.getAppValue<number>('basketMaxAge');
  }

  getExistingBookingCreatedDate(): Date | undefined {
    const existingBooking = this.order.existingBooking;
    if (!existingBooking) {
      return undefined;
    }

    return this.dateFormatService.dateStringToDate(existingBooking.createdDate);
  }
}
